/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REVIEW_COMPANY_REDESIGN
 * Date:         2026-02-10
 * Modified By:  Claude
 *
 * Description:
 * Redesigned ReviewCompanyForm to match mockup (setup-reviewcompany.html):
 * - Header card with left 4px teal accent border
 * - Two-column layout (1fr 380px grid)
 * - Card-based sections for Services, About, Contact, Details
 *
 * File Size Check:
 * - Current: ~280 lines
 * - Limit: 400 lines (page component)
 * - Decision: Within limit
 */
import React, { useContext } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { SetupContext } from "../Setup";
import type { Hours, ImageData, User } from "../../interfaces/User";
import styles from "./ReviewCompanyForm.module.scss";

interface ReviewCompanyFormProps {
  profile?: Partial<User> | null;
  showEditButton?: boolean;
  wrapInContainer?: boolean;
}

const getImageSrc = (img?: ImageData | null): string | null => {
  if (!img) return null;
  return img.data.startsWith("data:") ? img.data : `data:${img.contentType};base64,${img.data}`;
};

const formatAddress = (profile: Partial<User> | null): string => {
  if (!profile) return "";
  const parts = [profile.address1, profile.address2, profile.city, profile.state, profile.zip]
    .filter(Boolean)
    .join(", ");
  return parts || "";
};

const formatHours = (hours?: Hours): string => {
  if (!hours) return "Hours not provided";
  const { fromDay, toDay, from, to, timeZone } = hours;
  const hasAllHours = !!(fromDay && toDay && from && to && timeZone);
  if (!hasAllHours) return "Hours not provided";

  const dayPart = fromDay === toDay ? fromDay : `${fromDay} – ${toDay}`;
  const timePart = `${from} – ${to}`;
  let tzPart = timeZone;
  if (timeZone && timeZone.includes("/")) {
    const parts = timeZone.split("/");
    tzPart = parts[parts.length - 1].replace(/_/g, " ");
  }
  return `${dayPart}, ${timePart}, ${tzPart}`;
};

const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || "";
  const last = lastName?.charAt(0)?.toUpperCase() || "";
  return first + last || "?";
};

const ReviewCompanyForm: React.FC<ReviewCompanyFormProps> = ({
  profile: profileProp,
  showEditButton = true,
  wrapInContainer = true,
}) => {
  const setupContext = useContext(SetupContext);
  const profileFromContext = setupContext?.profile ?? null;
  const loadingFromContext = setupContext?.loading ?? false;
  const errorFromContext = setupContext?.error ?? null;

  const profile: Partial<User> | null = profileProp !== undefined ? profileProp : profileFromContext;
  const loading = profileProp !== undefined ? false : loadingFromContext;
  const error = profileProp !== undefined ? null : errorFromContext;

  const navigate = useNavigate();

  const handleEdit = () => {
    navigate("/setup?section=company");
  };

  const logo =
    (profile as any)?.companyLogos && (profile as any).companyLogos.length
      ? (profile as any).companyLogos[0]
      : undefined;

  const companyAddress = formatAddress(profile);
  const hoursText = formatHours(profile?.hours);

  if (loading && !profile) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  const pageContent = (
    <div className={styles.reviewB}>
      {/* Header Card with teal left accent */}
      <div className={styles.headerCard}>
        <div className={styles.logo}>
          {logo ? <img src={getImageSrc(logo) || ""} alt="Company logo" /> : "LOGO"}
        </div>
        <div className={styles.headerInfo}>
          <h1 className={styles.companyName}>{profile?.companyName || "Company name not set"}</h1>
          <div className={styles.companyLoc}>{profile?.country || "Country not set"}</div>
          {(profile?.companyLinkedIn || profile?.companyFacebook || profile?.companyInstagram || profile?.companyX) && (
            <div className={styles.socialRow}>
              {profile?.companyLinkedIn && (
                <a href={profile.companyLinkedIn} target="_blank" rel="noopener noreferrer" className={styles.socialIconB}>
                  <img src="/assets/social.svg" alt="LinkedIn" />
                </a>
              )}
              {profile?.companyFacebook && (
                <a href={profile.companyFacebook} target="_blank" rel="noopener noreferrer" className={styles.socialIconB}>
                  <img src="/assets/social (1).svg" alt="Facebook" />
                </a>
              )}
              {profile?.companyInstagram && (
                <a href={profile.companyInstagram} target="_blank" rel="noopener noreferrer" className={styles.socialIconB}>
                  <img src="/assets/social (2).svg" alt="Instagram" />
                </a>
              )}
              {profile?.companyX && (
                <a href={profile.companyX} target="_blank" rel="noopener noreferrer" className={styles.socialIconB}>
                  <img src="/assets/social (3).svg" alt="X" />
                </a>
              )}
            </div>
          )}
        </div>
        {showEditButton && (
          <button className={styles.editBtn} onClick={handleEdit} aria-label="Edit company profile">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M13.5 3.5l3 3M2 14.5V18h3.5L16.5 6.5l-3-3L2 14.5z" />
            </svg>
          </button>
        )}
      </div>

      {/* Two-column layout */}
      <div className={styles.columns}>
        {/* Main column - Services & About */}
        <div className={styles.main}>
          {/* Our Services */}
          <div className={styles.cardB}>
            <div className={styles.sectionLabel}>Services</div>
            <div className={styles.sectionTitleB}>Our Services</div>
            <p className={styles.sectionTextB}>
              {profile?.services || "Describe the main services your company provides."}
            </p>
          </div>

          {/* About Company */}
          <div className={styles.cardB}>
            <div className={styles.sectionLabel}>About</div>
            <div className={styles.sectionTitleB}>About {profile?.companyName || "Company"}</div>
            <p className={styles.sectionTextB}>
              {(profile as any)?.aboutCompany || "You haven't added information about your company yet."}
            </p>
            {Array.isArray((profile as any)?.certifications) && (profile as any).certifications.length > 0 && (
              <div className={styles.certifications}>
                <div className={styles.certTitle}>Our Certifications</div>
                <div className={styles.certRow}>
                  {(profile as any).certifications.map((cert: ImageData, idx: number) => (
                    <div key={idx} className={styles.certBadge}>
                      <img src={getImageSrc(cert) || ""} alt={`Certification ${idx + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Aside column - Contact & Details */}
        <div className={styles.aside}>
          {/* Main Contact */}
          <div className={styles.cardBLight}>
            <div className={styles.sectionLabel}>Main Contact</div>
            <div className={styles.contactMini}>
              <div className={styles.contactMiniAvatar}>
                {profile?.profileImages ? (
                  <img src={getImageSrc(profile.profileImages) || ""} alt="Profile" />
                ) : (
                  getInitials(profile?.firstName, profile?.lastName)
                )}
              </div>
              <div>
                {profile?._id ? (
                  <Link to={`/profile/${profile._id}?tab=profile`} className={styles.contactMiniNameLink}>
                    {profile?.firstName || profile?.lastName
                      ? `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim()
                      : "Name not set"}
                  </Link>
                ) : (
                  <div className={styles.contactMiniName}>
                    {profile?.firstName || profile?.lastName
                      ? `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim()
                      : "Name not set"}
                  </div>
                )}
                {profile?.jobTitle && <div className={styles.contactMiniRole}>{profile.jobTitle}</div>}
              </div>
            </div>
            <div className={styles.contactMiniLinks}>
              {profile?.email && (
                <div className={styles.contactMiniLink}>
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path d="M18 4H2C.9 4 0 4.9 0 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                  {profile.email}
                </div>
              )}
              {profile?.tel && (
                <div className={styles.contactMiniLink}>
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path d="M18.364 15.536l-3.536-3.536c-.39-.39-1.024-.39-1.414 0l-1.414 1.414c-.39.39-1.024.39-1.414 0L4.93 7.757c-.39-.39-.39-1.024 0-1.414l1.414-1.414c.39-.39.39-1.024 0-1.414L2.808.08c-.39-.39-1.024-.39-1.414 0L.08 1.394C-.5 1.974-.5 6.026 4.737 11.263s9.29 5.237 9.87 4.657l1.314-1.314c.39-.39.39-1.024 0-1.414z" />
                  </svg>
                  {profile.tel}
                </div>
              )}
            </div>
          </div>

          {/* Company Details */}
          <div className={styles.cardB}>
            <div className={styles.sectionLabel}>Company Details</div>
            <div className={styles.kvList}>
              <div className={styles.kvRow}>
                <span className={styles.kvKey}>Website</span>
                <span className={styles.kvVal}>
                  {profile?.website ? (
                    <a href={profile.website} target="_blank" rel="noopener noreferrer">
                      {profile.website.replace(/^https?:\/\//, "")}
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </span>
              </div>
              <div className={styles.kvRow}>
                <span className={styles.kvKey}>Email</span>
                <span className={styles.kvVal}>{profile?.companyEmail || "Not provided"}</span>
              </div>
              <div className={styles.kvRow}>
                <span className={styles.kvKey}>Phone</span>
                <span className={styles.kvVal}>{profile?.telCompany || "Not provided"}</span>
              </div>
              <div className={styles.kvRow}>
                <span className={styles.kvKey}>Address</span>
                <span className={styles.kvVal}>{companyAddress || "Not provided"}</span>
              </div>
              <div className={styles.kvRow}>
                <span className={styles.kvKey}>Hours</span>
                <span className={styles.kvVal}>{hoursText}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (wrapInContainer) {
    return <div className="container" style={{ maxWidth: "100%", width: "100%" }}>{pageContent}</div>;
  }
  return pageContent;
};

export default ReviewCompanyForm;
